import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface FacebookProfileViewProps {
  selectedPage: any;
  isDropdownOpen: boolean;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  pagesLoading: boolean;
  displayPages: any[];
  onPageSelect: (pageId: string) => void;
  userInfoEmail?: string;
  userInfoName?: string;
}

const FacebookProfileView: React.FC<FacebookProfileViewProps> = ({
  selectedPage,
  isDropdownOpen,
  dropdownRef,
  buttonRef,
  pagesLoading,
  displayPages,
  onPageSelect,
  userInfoEmail,
  userInfoName,
}) => {
  return (
    <div className="bg-[#2a2a2a] rounded-2xl border border-gray-700 overflow-hidden">
      <div className="relative h-48 bg-gradient-to-br from-gray-700 to-gray-800">
        {selectedPage.cover?.source ? (
          <img
            src={selectedPage.cover.source}
            alt="Cover"
            className="w-full h-full object-cover"
            style={{
              objectPosition: `50% ${selectedPage.cover.offset_y || 50}%`,
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
            <span className="text-white text-lg font-semibold">{selectedPage.name}</span>
          </div>
        )}

        <div className="absolute -bottom-12 left-6">
          <div className="w-24 h-24 rounded-full border-4 border-[#2a2a2a] bg-gray-700 overflow-hidden">
            {selectedPage.picture?.data?.url ? (
              <img
                src={selectedPage.picture.data.url}
                alt={selectedPage.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-500 to-red-600">
                <span className="text-white text-3xl font-bold">
                  {selectedPage.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isDropdownOpen && buttonRef.current && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed w-96 bg-[#212121] rounded-xl border border-gray-600 shadow-2xl z-50 max-h-[calc(100vh-200px)] overflow-y-auto"
            style={{
              top: `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
              right: `${window.innerWidth - buttonRef.current.getBoundingClientRect().right}px`,
            }}
          >
            <div className="p-2">
              {pagesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
                </div>
              ) : displayPages.length > 0 ? (
                displayPages.map((page: any) => (
                  <button
                    key={page.id}
                    onClick={() => onPageSelect(page.id)}
                    className="w-full cursor-pointer flex items-center gap-4 p-3 rounded-lg hover:bg-[#61616120] transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {page.picture?.data?.url ? (
                        <img
                          src={page.picture.data.url}
                          alt={page.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-gray-300 text-md font-semibold">
                          {page.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white text-sm font-medium truncate">{page.name}</h3>
                    </div>
                  </button>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No pages available</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Page Details */}
      <div className="p-6 pt-16">
        <h2 className="text-white text-2xl font-semibold mb-1">{selectedPage.name}</h2>
        <p className="text-gray-400 text-sm mb-4">
          {selectedPage.category_list?.[0]?.name || selectedPage.category || 'Public Figure'}
        </p>

        {selectedPage.about && (
          <p className="text-gray-300 text-sm mb-6 leading-relaxed">{selectedPage.about}</p>
        )}

        {userInfoEmail && (
          <div className="mb-4">
            <p className="text-gray-400 text-xs mb-1">Email</p>
            <p className="text-white text-sm">{userInfoEmail}</p>
          </div>
        )}

        {userInfoName && (
          <div className="mb-4">
            <p className="text-gray-400 text-xs mb-1">Connected Account</p>
            <p className="text-white text-sm">{userInfoName}</p>
          </div>
        )}

        {!userInfoEmail && selectedPage.emails && selectedPage.emails.length > 0 && (
          <div className="mb-4">
            <p className="text-gray-400 text-xs mb-1">Page Email</p>
            <p className="text-white text-sm">{selectedPage.emails[0]}</p>
          </div>
        )}

        {selectedPage.phone && (
          <div>
            <p className="text-gray-400 text-xs mb-1">Phone</p>
            <p className="text-white text-sm">{selectedPage.phone}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FacebookProfileView;
